import { isAxiosError } from 'axios'
import type { ApiErrorBody, ApiViolation } from '@/types/api'

/** Normalised API failure: always has a status, a machine code and a human message. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly violations: ApiViolation[]

  constructor(status: number, code: string, message: string, violations: ApiViolation[] = []) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.violations = violations
  }

  /** Field → message map, e.g. for React Hook Form's setError. */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.violations.map((v) => [v.field, v.message]))
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === 'object' && value !== null && 'error' in value && typeof value.error === 'object'
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  if (isAxiosError(error)) {
    const body: unknown = error.response?.data
    if (isErrorBody(body)) {
      return new ApiError(body.error.status, body.error.code, body.error.message, body.error.violations ?? [])
    }
    if (!error.response) {
      return new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection and try again.')
    }
    return new ApiError(error.response.status, 'http_error', error.message)
  }
  return new ApiError(0, 'unknown', error instanceof Error ? error.message : 'Something went wrong.')
}

export function errorMessage(error: unknown): string {
  return toApiError(error).message
}
